<script lang="ts">
  // Persistent header per INTERFACE-REVIEW.md §5 R1.
  //
  // Three concern groups, scoped to "applies everywhere":
  //   - Title block (left).
  //   - Catalogue freshness (Refreshed Xs ago + Refresh) — visible only
  //     on routes that consume the manifest (Dashboard, Catalogue,
  //     Changes, History). Hidden on Guide and Governance per R5.
  //   - Guide shortcut + Identity readout (right).
  //
  // Surface and View were demoted to the SurfaceViewToolbar component
  // (mounted by Catalogue.svelte above Matrix and Comparison only).
  // Dev identity moved to DevToolsDrawer (collapsible bottom-left,
  // still gated on import.meta.env.DEV).

  import { onMount } from 'svelte';
  import { identity } from '../lib/identity.svelte';
  import { isActive, route } from '../lib/router.svelte';
  import { catalogueState, refreshLiveCataloguesNow } from '../lib/catalogue-store.svelte';
  import { relativeTimeAgo } from '../lib/formatters';

  const roleSummary = $derived(identity.roles.join(' · '));

  // Refresh is meaningful only where the route consumes the manifest.
  // Guide is pure documentation; Governance reads access-control and
  // dimension data that are bundled at build time. Hiding the button
  // on those routes removes a control whose effect would be invisible
  // (no surfaces to re-fetch) — per INTERFACE-REVIEW.md §5 R5.
  const showsRefresh = $derived(
    route.current.name === 'dashboard'
      || route.current.name === 'catalogue'
      || route.current.name === 'changes'
      || route.current.name === 'history'
  );

  // Heartbeat ticks every 30 seconds so the "Refreshed X ago" chip
  // updates its relative-time string without a per-fetch refresh.
  // Without this, the chip would read "Just now" until the next
  // bootstrapManifest fired (focus or manual click) — fine for the
  // navigate-back pattern but stale for a user staring at one tab.
  let nowMs = $state<number>(Date.now());
  onMount(() => {
    const intervalId = window.setInterval(() => { nowMs = Date.now(); }, 30_000);
    return () => window.clearInterval(intervalId);
  });

  const refreshedLabel = $derived<string | null>(
    catalogueState.lastRefreshedAt
      ? `Refreshed ${relativeTimeAgo(new Date(catalogueState.lastRefreshedAt).toISOString(), nowMs)}`
      : null
  );

  async function handleRefreshClick() {
    await refreshLiveCataloguesNow();
  }
</script>

<header class="app-header">
  <div class="app-header__top">
    <div>
      <h1 class="app-header__title">
        Calculator Source-of-Truth
        <span class="app-header__title-sub">Maintenance interface</span>
      </h1>
    </div>

    <div class="app-header__controls">
      {#if showsRefresh}
        <div class="catalogue-freshness" aria-live="polite">
          {#if refreshedLabel}
            <span class="catalogue-freshness__label muted">{refreshedLabel}</span>
          {/if}
          <button
            type="button"
            class="catalogue-freshness__refresh"
            onclick={handleRefreshClick}
            disabled={catalogueState.refreshing}
            title="Re-fetch the manifest and every cached live catalogue"
          >
            {catalogueState.refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      {/if}

      <a
        href="/guide"
        class="guide-link"
        class:is-active={isActive('/guide')}
        title="Open the Guide — walkthroughs filtered to your role"
        aria-label="Open the Guide"
      >?</a>

      <div class="identity">
        {#if identity.loading}
          <span class="muted">…</span>
        {:else if identity.email}
          <span class="identity__email">{identity.display_name ?? identity.email}</span>
          <span class="identity__roles">{roleSummary || '(no roles)'}</span>
        {:else}
          <span class="muted">Not authenticated</span>
        {/if}
      </div>
    </div>
  </div>

  <nav class="app-nav" aria-label="Top-level navigation">
    <a href="/dashboard" class:is-active={isActive('/') || isActive('/dashboard')}>Dashboard</a>
    <a href="/catalogue" class:is-active={isActive('/catalogue')}>Catalogue</a>
    <a href="/changes" class:is-active={isActive('/changes')}>Changes</a>
    <a href="/history" class:is-active={isActive('/history')}>History</a>
    <a href="/governance" class:is-active={isActive('/governance')}>Governance</a>
    <a href="/guide" class:is-active={isActive('/guide')}>Guide</a>
  </nav>

  {#if route.current.name === 'governance' && route.current.params.panel}
    <div class="muted" style="font-size: var(--type-sm)">
      Governance · {route.current.params.panel}
    </div>
  {/if}
</header>
